import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppEventBus from './services/AppEventBus';
import JerseySlot, { getJerseyImage } from './components/JerseySlot';
import BasketballCourt from './components/BasketballCourt';
import { API_BASE_URL } from './config';
import { formatDateTime } from './utils/dateUtils';

// 5 titulares: 2 interiores arriba, 3 perimetrales abajo
const starters = [
  { label: 'INTERIOR',   x: 30, y: 28, positionType: 'Interior' },
  { label: 'INTERIOR',   x: 70, y: 28, positionType: 'Interior' },
  { label: 'PERIMETRAL', x: 15, y: 72, positionType: 'Perimetral' },
  { label: 'PERIMETRAL', x: 50, y: 78, positionType: 'Perimetral' },
  { label: 'PERIMETRAL', x: 85, y: 72, positionType: 'Perimetral' },
];

// 5 suplentes: 3 perimetrales, 2 interior
const bench = [
  { label: 'PERIMETRAL', positionType: 'Perimetral' },
  { label: 'PERIMETRAL', positionType: 'Perimetral' },
  { label: 'PERIMETRAL', positionType: 'Perimetral' },
  { label: 'INTERIOR', positionType: 'Interior' },
  { label: 'INTERIOR', positionType: 'Interior' },
];

// El rango de precios se calculará dinámicamente basado en los jugadores disponibles
const getDefaultTransferPriceRange = (players: any[]): [number, number] => {
  if (!players || players.length === 0) {
    return [4, 20]; // Rango por defecto si no hay jugadores
  }
  const prices = players.map(p => parseFloat(p.price || 0)).filter(p => p > 0);
  if (prices.length === 0) {
    return [4, 20];
  }
  const minPrice = Math.floor(Math.min(...prices));
  const maxPrice = Math.ceil(Math.max(...prices));
  // Asegurar un mínimo de rango razonable
  return [Math.max(4, minPrice), Math.max(15, maxPrice + 1)];
};

const formatNumber = (value: any, decimals = 1): string => {
  const num = typeof value === 'number' ? value : parseFloat(value ?? '0');
  if (!Number.isFinite(num)) {
    return (0).toFixed(decimals);
  }
  return num.toFixed(decimals);
};

const cloneAssignedMap = (map: { [key: string]: any }) => JSON.parse(JSON.stringify(map || {}));

const MyTeamCompetitionPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Estilos para el slider de rango dual
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      input[type="range"] {
        -webkit-appearance: none;
        appearance: none;
        background: transparent;
      }
      input[type="range"]::-webkit-slider-track {
        background: transparent;
        height: 8px;
      }
      input[type="range"]::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: #3b82f6;
        cursor: pointer;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        margin-top: -8px;
      }
      input[type="range"]::-moz-range-track {
        background: transparent;
        height: 8px;
      }
      input[type="range"]::-moz-range-thumb {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: #3b82f6;
        cursor: pointer;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  // Estados necesarios para el componente
  const [assigned, setAssigned] = useState<{ [key: string]: any }>({});
  const [modalError, setModalError] = useState<string | null>(null);
  const [budget, setBudget] = useState<number>(100);
  
  // Estados para transferencias
  const [transfersUsed, setTransfersUsed] = useState(0);
  const [maxTransfers] = useState(2); // setMaxTransfers no se usa
  const [currentMatchday, setCurrentMatchday] = useState<any>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedPlayerToTransfer, setSelectedPlayerToTransfer] = useState<any>(null);
  const [availablePlayers, setAvailablePlayers] = useState<any[]>([]);
  const [loadingAvailablePlayers, setLoadingAvailablePlayers] = useState(false);
  const [showTransferConfirmation, setShowTransferConfirmation] = useState(false);
  const [playerToTransferIn, setPlayerToTransferIn] = useState<any>(null);
  
  // Estados para intercambio compensatorio (reservados para uso futuro)
  const [, setNeedsCompensatorySwap] = useState(false);
  const [, setCompensatorySwapOut] = useState<any>(null);
  const [, setCompensatorySwapIn] = useState<any>(null);
  const [, setAvailableCompensatoryPlayers] = useState<any[]>([]);
  
  // Estados para filtros de transferencia
  const [transferSearchTerm, setTransferSearchTerm] = useState('');
  const [transferCategoryFilter, setTransferCategoryFilter] = useState<string[]>([]);
  const [transferSelectedClubs, setTransferSelectedClubs] = useState<string[]>([]);
  const [transferSelectedPosition, setTransferSelectedPosition] = useState<string>('');
  const [, setDefaultTransferPriceRange] = useState<[number, number]>([4, 20]);
  const [transferPriceRange, setTransferPriceRange] = useState<[number, number]>([4, 20]); // [min, max]
  const [transferSort] = useState<'none' | 'price_asc' | 'price_desc' | 'fantasy_asc' | 'fantasy_desc' | 'avg_asc' | 'avg_desc'>('price_desc');
  const [showTransferClubsDropdown, setShowTransferClubsDropdown] = useState(false);
  const [showTransferPositionDropdown, setShowTransferPositionDropdown] = useState(false);
  // Estados para información de jornada
  const [transferDeadline, setTransferDeadline] = useState<string>('');
  const [matchdayStartDate, setMatchdayStartDate] = useState<string>('');
  
  // Estados para modal de partidos
  const [showMatchesModal, setShowMatchesModal] = useState(false);
  const [fixtures, setFixtures] = useState<any[]>([]);
  const [loadingFixtures, setLoadingFixtures] = useState(false);

  // Estados para el modal de sustitución (igual que en TeamCourtPage)
  const [showSubstitutionModal, setShowSubstitutionModal] = useState(false);
  const [substitutionMode, setSubstitutionMode] = useState<'remove' | 'substitute' | 'captain' | null>(null);
  const [playerToRemove, setPlayerToRemove] = useState<any>(null);
  const [slotToRemove, setSlotToRemove] = useState<{type: 'starter' | 'bench', idx: number} | null>(null);
  const [isProcessingSubstitution, setIsProcessingSubstitution] = useState(false);
  const [timelineMatchdays, setTimelineMatchdays] = useState<any[]>([]);
  const [nextMatchdayInfo, setNextMatchdayInfo] = useState<any>(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const timelineScrollRef = React.useRef<HTMLDivElement>(null);

  // Estados para el sistema de capitán
  const [captainId, setCaptainId] = useState<string | null>(null);
  const [captainLoaded, setCaptainLoaded] = useState<boolean>(false);

  // Estado para el nombre del equipo
  const [teamName, setTeamName] = useState('Mi Equipo');
  const [originalAssigned, setOriginalAssigned] = useState<{ [key: string]: any }>({});
  const [originalCaptainId, setOriginalCaptainId] = useState<string | null>(null);
  const [pendingTransfers, setPendingTransfers] = useState<Array<{ slotKey: string; playerOut: any; playerIn: any }>>([]);
  const [draftBudget, setDraftBudget] = useState<number | null>(null);
  const [isApplyingTransfers, setIsApplyingTransfers] = useState(false);

  const teamId = localStorage.getItem('fantasyTeamId');

  // Función para traducir mensajes de error del backend
  const translateError = useCallback((errorMessage: string): string => {
    const errorTranslations: { [key: string]: string } = {
      'cannot add more than 3 extranejro players': 'No se pueden agregar más de 3 jugadores extranjeros',
      'cannot add more than 3 extranjero players': 'No se pueden agregar más de 3 jugadores extranjeros',
      'cannot add more than 5 fichanacional players': 'No se pueden agregar más de 5 jugadores de ficha nacional',
      'cannot add more than 5 ficha nacional players': 'No se pueden agregar más de 5 jugadores de ficha nacional',
      'cannot add more than 2 sub23 players': 'No se pueden agregar más de 2 jugadores Sub 23',
      'cannot add more than 3 perimetral players': 'No se pueden agregar más de 3 jugadores perimetrales',
      'cannot add more than 2 interior players': 'No se pueden agregar más de 2 jugadores interiores',
      'cannot add more than 3 players from': 'Ya tienes 3 jugadores de este equipo. Máximo 3 por club',
      'insufficient budget': 'Presupuesto insuficiente',
      'player already in team': 'El jugador ya está en el equipo',
      'player not found': 'Jugador no encontrado',
      'team not found': 'Equipo no encontrado',
      'invalid player data': 'Datos de jugador inválidos',
      'transfer limit exceeded': 'Límite de transferencias excedido',
      'transfer window closed': 'Ventana de transferencias cerrada',
      'Error adding player to team': 'Error al agregar jugador al equipo',
      'Error updating source player role': 'Error al actualizar rol del jugador origen',
      'Error updating target player role': 'Error al actualizar rol del jugador destino',
      'Error removing player from team': 'Error al eliminar jugador del equipo',
      'Error confirmando equipo': 'Error al confirmar equipo',
      'Network error': 'Error de conexión',
      'Error loading players': 'Error al cargar jugadores'
    };

    // Normalizar el mensaje para búsqueda
    const normalizedMessage = errorMessage.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
    
    // Buscar traducción exacta
    if (errorTranslations[normalizedMessage]) {
      return errorTranslations[normalizedMessage];
    }

    // Buscar traducción parcial para mensajes más complejos
    for (const [key, translation] of Object.entries(errorTranslations)) {
      if (normalizedMessage.includes(key) || errorMessage.toLowerCase().includes(key)) {
        return translation;
      }
    }

    // Si no hay traducción, devolver el mensaje original
    return errorMessage;
  }, []);

  // Contar jugadores asignados (no utilizado en esta página)
  // const assignedPlayersCount = useMemo(() => 
  //   Object.values(assigned).filter((p: any) => p !== null).length,
  //   [assigned]
  // );


  // Cargar capitán actual
  const fetchCaptain = useCallback(async () => {
    if (!teamId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/captain`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const captainId = data.data?.captainId || null;
        const normalizedCaptain = captainId ? captainId.toLowerCase() : null;
        setCaptainId(normalizedCaptain);
        setOriginalCaptainId(normalizedCaptain);
        setCaptainLoaded(true);
      }
    } catch (error) {
      console.error('Error fetching captain:', error);
    }
  }, [teamId]);

  // Designar capitán
  const handleSetCaptain = useCallback(async (playerId: string) => {
    if (!teamId) return;

    // DEBUGGING PARA PRODUCCIÓN
    console.log('🔍 [PROD] handleSetCaptain called');
    console.log('🔍 [PROD] playerId:', playerId);
    console.log('🔍 [PROD] currentMatchday:', currentMatchday);
    console.log('🔍 [PROD] cambiosAbiertos:', currentMatchday?.cambiosAbiertos);
    console.log('🔍 [PROD] tipo:', typeof currentMatchday?.cambiosAbiertos);
    console.log('🔍 [PROD] !currentMatchday:', !currentMatchday);
    console.log('🔍 [PROD] !currentMatchday.cambiosAbiertos:', !currentMatchday?.cambiosAbiertos);

    // Verificar si los cambios están abiertos
    if (currentMatchday && !currentMatchday.cambiosAbiertos) {
      console.log('❌ [PROD] BLOQUEADO: cambios cerrados');
      setModalError('Los cambios están cerrados para esta jornada');
      return;
    }
    
    console.log('✅ [PROD] PASÓ VALIDACIÓN: continuando...');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/captain`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ playerId })
      });
      
      if (response.ok) {
        setCaptainId(playerId.toLowerCase());
        setModalError(null);
        // Recargar el capitán desde el backend para asegurar consistencia
        await fetchCaptain();
      } else {
        const errorData = await response.json();
        setModalError(translateError(errorData.message || 'Error al designar capitán'));
      }
    } catch (error) {
      console.error('Error setting captain:', error);
      setModalError(translateError('Error de conexión'));
    }
  }, [teamId, translateError, fetchCaptain, currentMatchday]);

  // Obtener presupuesto y nombre del equipo del backend
  const fetchBudget = useCallback(async () => {
    if (!teamId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        console.log('💰 Presupuesto recibido del backend:', {
          budgetRemaining: data.budgetRemaining,
          budgetRemainingType: typeof data.budgetRemaining,
          confirmed: data.confirmed,
          teamName: data.name
        });
        // Cargar nombre del equipo
        if (data.name) {
          setTeamName(data.name);
        }
        // Cargar presupuesto
        if (typeof data.budgetRemaining === 'number') {
          setBudget(data.budgetRemaining);
          console.log('✅ Presupuesto actualizado en el frontend:', data.budgetRemaining);
        } else if (data.budgetRemaining !== null && data.budgetRemaining !== undefined) {
          // Si viene como string (puede pasar con DECIMAL de PostgreSQL)
          const budgetValue = parseFloat(data.budgetRemaining);
          if (!isNaN(budgetValue)) {
            setBudget(budgetValue);
            console.log('✅ Presupuesto actualizado en el frontend (desde string):', budgetValue);
          } else {
            console.warn('⚠️ Presupuesto no es un número válido:', data.budgetRemaining);
          }
        } else {
          console.warn('⚠️ Presupuesto es null o undefined');
        }
      } else {
        console.error('❌ Error al obtener presupuesto:', res.status, res.statusText);
      }
    } catch (error) {
      console.error('❌ Error en fetchBudget:', error);
    }
  }, [teamId]);

  // Cargar jugadores existentes del equipo
  const loadExistingPlayers = useCallback(async () => {
    if (!teamId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/players`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const playersResponse = await res.json();
        const players = playersResponse.data;
        
        console.log('Jugadores cargados del backend:', players);
        
        const newAssigned: { [key: string]: any } = {};
        
        // Separar jugadores por rol
        const titulares = players.filter((p: any) => p.role === 'Titular');
        const suplentes = players.filter((p: any) => p.role === 'Suplente');
        
        // Mapear titulares por posición exacta primero
        for (let i = 0; i < starters.length && titulares.length > 0; i++) {
          const slot = starters[i];
          const matchingPlayer = titulares.find((p: any) => 
            p.positionType === slot.positionType && 
            !Object.values(newAssigned).some(assigned => assigned?.id === p.id)
          );
          
          if (matchingPlayer) {
            newAssigned[`starter-${i}`] = matchingPlayer;
          }
        }
        
        // Si quedan titulares sin asignar, llenar slots vacíos
        const unassignedTitulares = titulares.filter((p: any) => 
          !Object.values(newAssigned).some(assigned => assigned?.id === p.id)
        );
        
        for (let i = 0; i < starters.length && unassignedTitulares.length > 0; i++) {
          if (!newAssigned[`starter-${i}`]) {
            const player = unassignedTitulares.shift();
            if (player) {
              newAssigned[`starter-${i}`] = player;
            }
          }
        }
        
        // Mapear suplentes por posición exacta primero
        for (let i = 0; i < bench.length && suplentes.length > 0; i++) {
          const slot = bench[i];
          const matchingPlayer = suplentes.find((p: any) => 
            p.positionType === slot.positionType && 
            !Object.values(newAssigned).some(assigned => assigned?.id === p.id)
          );
          
          if (matchingPlayer) {
            newAssigned[`bench-${i}`] = matchingPlayer;
          }
        }
        
        // Si quedan suplentes sin asignar, llenar slots vacíos
        const unassignedSuplentes = suplentes.filter((p: any) => 
          !Object.values(newAssigned).some(assigned => assigned?.id === p.id)
        );
        
        for (let i = 0; i < bench.length && unassignedSuplentes.length > 0; i++) {
          if (!newAssigned[`bench-${i}`]) {
            const player = unassignedSuplentes.shift();
            if (player) {
              newAssigned[`bench-${i}`] = player;
            }
          }
        }
        
        const clonedAssigned = cloneAssignedMap(newAssigned);
        setAssigned(clonedAssigned);
        setOriginalAssigned(cloneAssignedMap(newAssigned));
        setPendingTransfers([]);
        setDraftBudget(null);
      }
    } catch (error) {
      console.error('Error loading existing players:', error);
    }
  }, [teamId]);

  // Cargar información de la jornada actual
  const fetchCurrentMatchday = useCallback(async () => {
    console.log('🔵 [PROD] INICIANDO fetchCurrentMatchday');
    try {
      const token = localStorage.getItem('token');
      console.log('🔵 [PROD] Token existe:', !!token);
      console.log('🔵 [PROD] API_BASE_URL:', API_BASE_URL);
      
      const res = await fetch(`${API_BASE_URL}/api/matchdays/current`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      console.log('🔵 [PROD] Response status:', res.status);
      console.log('🔵 [PROD] Response ok:', res.ok);
      
      if (res.ok) {
        const data = await res.json();
        console.log('🔵 [PROD] fetchCurrentMatchday - Respuesta completa:', data);
        console.log('🔵 [PROD] fetchCurrentMatchday - data.data:', data.data);
        console.log('🔵 [PROD] fetchCurrentMatchday - cambiosAbiertos:', data.data?.cambiosAbiertos);
        setCurrentMatchday(data.data);
        
        // Calcular fecha límite de transferencias (15 minutos antes del inicio)
        if (data.data && data.data.startDate) {
          const startDate = new Date(data.data.startDate);
          const deadline = new Date(startDate.getTime() - 15 * 60 * 1000); // 15 minutos antes
          
          setMatchdayStartDate(formatDateTime(data.data.startDate));
          
          setTransferDeadline(formatDateTime(deadline.toISOString()));
        }
      } else {
        console.error('🔴 [PROD] fetchCurrentMatchday ERROR - Status:', res.status);
        const errorText = await res.text();
        console.error('🔴 [PROD] Error response:', errorText);
      }
    } catch (error) {
      console.error('🔴 [PROD] fetchCurrentMatchday EXCEPTION:', error);
    }
  }, []);

  // Función para calcular puntos totales de una fecha (igual que en FechasPage.tsx)
  const calculateMatchdayPoints = useCallback(async (matchdayId: string): Promise<number> => {
    if (!teamId) return 0;
    const token = localStorage.getItem('token');
    if (!token) return 0;

    try {
      // Obtener snapshot del equipo para esta fecha
      const snapshotResponse = await fetch(`${API_BASE_URL}/api/snapshots/team/${teamId}/matchday/${matchdayId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Si no hay snapshot (404), devolver 0 en lugar de null
      if (!snapshotResponse.ok) {
        if (snapshotResponse.status === 404) {
          return 0; // No hay snapshot para esta fecha, equipo no estaba conformado
        }
        // Otros errores también devuelven 0
        return 0;
      }

      const snapshotData = await snapshotResponse.json();
      if (!snapshotData.success || !snapshotData.data) {
        return 0;
      }

      const snapshot = snapshotData.data;

      // Función para obtener puntos de un jugador específico
      const getPlayerFantasyPoints = async (playerId: string): Promise<number> => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/player-stats/by-player-and-matchday?playerId=${playerId}&matchdayId=${matchdayId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              return data.data.fantasyPoints || 0;
            }
          }
          return 0;
        } catch (error) {
          return 0;
        }
      };

      // Obtener puntos para titulares
      const titularesWithPoints = await Promise.all(
        snapshot.titulares.map(async (player: any) => {
          const points = await getPlayerFantasyPoints(player.id);
          return {
            ...player,
            fantasyPoints: points
          };
        })
      );

      // Obtener puntos para suplentes
      const suplentesWithPoints = await Promise.all(
        snapshot.suplentes.map(async (player: any) => {
          const points = await getPlayerFantasyPoints(player.id);
          return {
            ...player,
            fantasyPoints: points
          };
        })
      );

      // Calcular total (igual que en FechasPage.tsx línea 470-471)
      const totalPoints = Math.round(
        titularesWithPoints.reduce((sum, p) => sum + (p.fantasyPoints || 0) * (p.isCaptain ? 1.5 : 1.0), 0) +
        suplentesWithPoints.reduce((sum, p) => sum + (p.fantasyPoints || 0) * 0.5, 0)
      );

      return totalPoints;
    } catch (error) {
      // Cualquier error devuelve 0
      return 0;
    }
  }, [teamId]);

  const fetchMatchdayTimeline = useCallback(async () => {
    if (!teamId) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoadingTimeline(true);
    setTimelineError(null);

    try {
      // Primero obtener el historial de snapshots para saber qué fechas tienen equipo conformado
      const historyRes = await fetch(`${API_BASE_URL}/api/snapshots/team/${teamId}/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      let matchdaysWithSnapshots = new Set<string>();
      if (historyRes.ok) {
        const historyData = await historyRes.json();
        if (historyData.success && historyData.data && historyData.data.history) {
          // Crear un Set con los matchdayIds que tienen snapshots
          historyData.data.history.forEach((item: any) => {
            if (item.matchdayId) {
              matchdaysWithSnapshots.add(item.matchdayId);
            }
          });
        }
      }

      // Ahora obtener todas las fechas
      const res = await fetch(`${API_BASE_URL}/api/matchdays?limit=40`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('Error al cargar matchdays para timeline:', errorText);
        throw new Error('No se pudieron cargar las jornadas');
      }

      const data = await res.json();
      const matchdays = data.data?.data || data.data || [];
      const sortedMatchdays = [...matchdays].sort(
        (a: any, b: any) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      );

      const currentId = currentMatchday?.id;
      const upcomingMatchday = sortedMatchdays.find((md: any) => md.status === 'pending');
      setNextMatchdayInfo(upcomingMatchday || null);

      // Crear timeline items y calcular puntos solo para fechas que tienen snapshots
      const timelineItemsPromises = sortedMatchdays.map(async (md: any, index: number) => {
        let type: 'past' | 'current' | 'next' | 'future' = 'future';

        if (currentId && md.id === currentId) {
          type = 'current';
        } else if (upcomingMatchday && md.id === upcomingMatchday.id) {
          type = 'next';
        } else if (md.status === 'completed') {
          type = 'past';
        } else if (md.status === 'in_progress') {
          type = 'current';
        }

        // Calcular puntos solo si:
        // 1. La fecha está completada o en progreso
        // 2. Y tiene snapshot (equipo conformado)
        let points: number = 0;
        if ((md.status === 'completed' || md.status === 'in_progress') && matchdaysWithSnapshots.has(md.id)) {
          points = await calculateMatchdayPoints(md.id);
        }

        return {
          id: md.id,
          name: md.name,
          status: md.status,
          startDate: md.startDate,
          endDate: md.endDate,
          order: index,
          type,
          points,
        };
      });

      const timelineItems = await Promise.all(timelineItemsPromises);
      setTimelineMatchdays(timelineItems);
    } catch (error) {
      console.error('fetchMatchdayTimeline error:', error);
      setTimelineError('No pudimos cargar las jornadas. Reintentá más tarde.');
    } finally {
      setLoadingTimeline(false);
    }
  }, [teamId, currentMatchday?.id, calculateMatchdayPoints]);

  const handleViewMatchdayInFechas = useCallback((matchdayId: string) => {
    navigate(`/Fechas?matchdayId=${matchdayId}`);
    AppEventBus.emit('focus-fechas', matchdayId);
  }, [navigate]);
  // Cargar transferencias usadas
  const fetchTransfersUsed = useCallback(async () => {
    if (!teamId || !currentMatchday) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/transfers/${currentMatchday.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTransfersUsed(data.transfersUsed || 0);
      }
    } catch (error) {
      console.error('Error loading transfers used:', error);
    }
  }, [teamId, currentMatchday]);

  // Cargar jugadores disponibles para transferencias
  const fetchAvailablePlayers = useCallback(async (playerToTransfer: any) => {
    setLoadingAvailablePlayers(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/players`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const assignedPlayerIds = Object.values(assigned).map(p => p?.id).filter(Boolean);
        
        // Filtrar jugadores disponibles según las reglas de transferencia
        // Permitir jugadores de la misma categoría (misma posición o diferente para intercambio compensatorio)
        const available = data.data.filter((p: any) => {
          // No debe estar ya en el equipo
          if (assignedPlayerIds.includes(p.id)) return false;
          
          // Debe tener la misma categoría (Extranjero, FichaNacional, Sub23)
          if (p.category !== playerToTransfer.category) return false;
          
          // Puede tener la misma posición (transferencia directa) o diferente (requiere intercambio compensatorio)
          return true;
        });
        
        // Actualizar ambos estados juntos para evitar renders innecesarios
        setAvailablePlayers(available);
        // Calcular y actualizar el rango de precios dinámicamente
        const dynamicRange = getDefaultTransferPriceRange(available);
        setDefaultTransferPriceRange(dynamicRange);
        // Actualizar el rango actual solo si es la primera carga (valores por defecto)
        setTransferPriceRange(prev => {
          // Si el rango actual es el inicial [4, 20], actualizarlo al dinámico
          if (prev[0] === 4 && prev[1] === 20) {
            return dynamicRange;
          }
          // Si el rango actual está fuera del nuevo rango dinámico, ajustarlo
          if (prev[0] < dynamicRange[0] || prev[1] > dynamicRange[1]) {
            return [
              Math.max(dynamicRange[0], prev[0]),
              Math.min(dynamicRange[1], prev[1])
            ];
          }
          return prev;
        });
        setLoadingAvailablePlayers(false);
      } else {
        setLoadingAvailablePlayers(false);
      }
    } catch (error) {
      console.error('Error loading available players:', error);
      setLoadingAvailablePlayers(false);
    }
  }, [assigned]);

  // Función para formatear nombres de equipos
  const formatTeamName = (teamName: string) => {
    const teamNames: { [key: string]: string } = {
      'penarol': 'Peñarol',
      'PENAROL': 'Peñarol',
      'Penarol': 'Peñarol',
      'aguada': 'Aguada',
      'malvin': 'Malvín',
      'welcome': 'Welcome',
      'union atletica': 'Unión Atlética',
      'cordon': 'Cordón',
      'hebraica y macabi': 'Hebraica y Macabi',
      'goes': 'Goes',
      'nacional': 'Nacional',
      'defensor': 'Defensor',
      'urunday': 'Urunday',
      'bigua': 'Biguá'
    };
    
    const lowerName = teamName.toLowerCase();
    return teamNames[lowerName] || teamName;
  };

  // Función para obtener fixtures de la jornada actual
  const fetchFixtures = useCallback(async (matchdayName: string) => {
    try {
      setLoadingFixtures(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/fixtures/matchday/${encodeURIComponent(matchdayName)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        setFixtures(data.data || []);
      } else {
        console.error('Error fetching fixtures:', res.statusText);
        setFixtures([]);
      }
    } catch (error) {
      console.error('Error loading fixtures:', error);
      setFixtures([]);
    } finally {
      setLoadingFixtures(false);
    }
  }, []);

  const getOpponentForTeam = useCallback(
    (teamName: string) => {
      if (!teamName || fixtures.length === 0) return null;

      const normalizedTeam = teamName.toLowerCase();
      const match = fixtures.find((fixture: any) => {
        const localTeam = fixture.localTeam?.toLowerCase();
        const visitingTeam = fixture.visitingTeam?.toLowerCase();
        return localTeam === normalizedTeam || visitingTeam === normalizedTeam;
      });

      if (!match) return null;

      const isHome = match.localTeam?.toLowerCase() === normalizedTeam;
      const opponentRaw = isHome ? match.visitingTeam : match.localTeam;
      const matchDate =
        match.startDate ||
        match.matchDate ||
        match.date ||
        match.gameDate ||
        match.localDate;

      return {
        opponent: formatTeamName(opponentRaw),
        condition: isHome ? 'de local' : 'de visitante',
        date: matchDate,
      };
    },
    [fixtures]
  );

  useEffect(() => {
    if (
      (showSubstitutionModal || showTransferModal) &&
      currentMatchday?.name &&
      fixtures.length === 0 &&
      !loadingFixtures
    ) {
      fetchFixtures(currentMatchday.name);
    }
  }, [showSubstitutionModal, showTransferModal, currentMatchday, fixtures.length, loadingFixtures, fetchFixtures]);

  // Cerrar dropdowns al hacer clic fuera
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showTransferClubsDropdown && !target.closest('.transfer-clubs-dropdown-container')) {
        setShowTransferClubsDropdown(false);
      }
      if (showTransferPositionDropdown && !target.closest('.transfer-position-dropdown-container')) {
        setShowTransferPositionDropdown(false);
      }
    };

    if (showTransferClubsDropdown || showTransferPositionDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTransferClubsDropdown, showTransferPositionDropdown]);

  const upcomingOpponent = useMemo(() => {
    if (!playerToRemove) return null;
    return getOpponentForTeam(playerToRemove.team);
  }, [playerToRemove, getOpponentForTeam]);

  // Validar si un cambio requiere intercambio compensatorio
  const validateTransferNeedsCompensatory = useCallback((playerOut: any, playerIn: any) => {
    // Si tienen la misma posición, no necesita intercambio compensatorio
    if (playerOut.positionType === playerIn.positionType) {
      return { needsSwap: false };
    }
    
    // Simular el cambio para ver si viola restricciones
    const tempAssigned = { ...assigned };
    Object.keys(tempAssigned).forEach(key => {
      if (tempAssigned[key]?.id === playerOut.id) {
        tempAssigned[key] = playerIn;
      }
    });
    
    // Contar jugadores por posición
    const allPlayers = Object.values(tempAssigned).filter(Boolean) as any[];
    const positionCounts = {
      Perimetral: allPlayers.filter(p => p.positionType === 'Perimetral').length,
      Interior: allPlayers.filter(p => p.positionType === 'Interior').length
    };
    
    // Si viola las restricciones de posición, necesita intercambio compensatorio
    if (positionCounts.Perimetral > 6 || positionCounts.Interior > 4) {
      // Determinar qué posición necesita compensación
      const oppositePosition = playerOut.positionType === 'Perimetral' ? 'Interior' : 'Perimetral';
      return { 
        needsSwap: true, 
        oppositePosition,
        reason: `Cambiar ${playerOut.name} (${playerOut.positionType}) por ${playerIn.name} (${playerIn.positionType}) violaría las restricciones de posición. Necesitas también cambiar un ${oppositePosition} por un ${playerOut.positionType}.`
      };
    }
    
    return { needsSwap: false };
  }, [assigned]);

  // Buscar jugadores disponibles para intercambio compensatorio (reservado para uso futuro)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fetchCompensatoryPlayers = useCallback(async (playerOut: any, playerIn: any) => {
    const validation = validateTransferNeedsCompensatory(playerOut, playerIn);
    if (!validation.needsSwap || !validation.oppositePosition) {
      setAvailableCompensatoryPlayers([]);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/players`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const assignedPlayerIds = Object.values(assigned).map(p => p?.id).filter(Boolean);
        
        // Buscar jugadores del equipo con la posición opuesta
        const playersToSwapOut = Object.entries(assigned)
          .filter(([key, player]: [string, any]) => 
            player && 
            player.id !== playerOut.id &&
            player.positionType === validation.oppositePosition
          )
          .map(([key, player]: [string, any]) => ({ slotKey: key, player }));
        
        // Buscar jugadores disponibles con la posición que necesita entrar
        const playersToSwapIn = data.data.filter((p: any) => {
          if (assignedPlayerIds.includes(p.id)) return false;
          if (p.id === playerIn.id) return false; // Ya está seleccionado
          if (p.positionType !== playerOut.positionType) return false; // Debe ser la posición opuesta
          if (p.category !== playerOut.category) return false; // Misma categoría para mantener balance
          return true;
        });
        
        setAvailableCompensatoryPlayers(playersToSwapIn);
        setCompensatorySwapOut(playersToSwapOut.length > 0 ? playersToSwapOut[0] : null);
      }
    } catch (error) {
      // Error loading compensatory players
    }
  }, [assigned, validateTransferNeedsCompensatory]);

  // Mostrar modal de confirmación de transferencia
  const showTransferConfirmationModal = (playerOut: any, playerIn: any) => {
    setPlayerToTransferIn(playerIn);
    
    // NO validar intercambio compensatorio aquí - solo permitir la transferencia
    // La validación se hará al aplicar las transferencias
    setNeedsCompensatorySwap(false);
    setCompensatorySwapOut(null);
    setCompensatorySwapIn(null);
    
    setShowTransferConfirmation(true);
  };

  const discardPendingTransfers = useCallback(() => {
    setPendingTransfers([]);
    setAssigned(cloneAssignedMap(originalAssigned));
    setDraftBudget(null);
    if (originalCaptainId) {
      setCaptainId(originalCaptainId);
    }
    setModalError(null);
    setSelectedPlayerToTransfer(null);
    setPlayerToTransferIn(null);
  }, [originalAssigned, originalCaptainId]);

  // Descartar una transferencia individual
  const discardSingleTransfer = useCallback((slotKey: string) => {
    const updatedTransfers = pendingTransfers.filter(transfer => transfer.slotKey !== slotKey);
    setPendingTransfers(updatedTransfers);
    
    // Recalcular el estado asignado sin la transferencia descartada
    const updatedAssigned = cloneAssignedMap(originalAssigned);
    updatedTransfers.forEach((transfer) => {
      updatedAssigned[transfer.slotKey] = { ...transfer.playerIn };
    });
    setAssigned(updatedAssigned);
    
    // Recalcular el presupuesto
    if (updatedTransfers.length === 0) {
      setDraftBudget(null);
    } else {
      const totalDiff = updatedTransfers.reduce((sum, transfer) => {
        const out = parseFloat(transfer.playerOut.price ?? transfer.playerOut.transferPrice ?? '0');
        const inside = parseFloat(transfer.playerIn.price ?? '0');
        const safeOutDiff = Number.isFinite(out) ? out : 0;
        const safeInDiff = Number.isFinite(inside) ? inside : 0;
        return sum + safeOutDiff - safeInDiff;
      }, 0);
      setDraftBudget(parseFloat((budget + totalDiff).toFixed(1)));
    }
  }, [pendingTransfers, originalAssigned, budget]);

  const queueTransfer = (playerOut: any, playerIn: any, compensatorySwapOut?: any, compensatorySwapIn?: any) => {
    if (!playerOut || !playerIn) return;

    const slotKey = selectedPlayerToTransfer?.slotKey;
    if (!slotKey) {
      setModalError('No se pudo identificar la posición del jugador a reemplazar. Intenta nuevamente.');
      return;
    }

    // NO validar intercambio compensatorio aquí - permitir la transferencia y validar al aplicar
    const existingIndex = pendingTransfers.findIndex((transfer) => transfer.slotKey === slotKey);
    
    // Contar transferencias efectivas (excluyendo las que se van a reemplazar)
    let effectivePendingCount = pendingTransfers.length;
    if (existingIndex >= 0) effectivePendingCount--;
    
    // Validar límite de transferencias (solo la principal por ahora)
    if (transfersUsed + effectivePendingCount + 1 > maxTransfers) {
      setModalError(`Ya alcanzaste el límite de ${maxTransfers} transferencias para esta jornada.`);
      return;
    }

    // Validar si los cambios están abiertos usando cambiosAbiertos de la BD
    if (currentMatchday && !currentMatchday.cambiosAbiertos) {
      const transferDeadline = new Date(new Date(currentMatchday.startDate).getTime() - 15 * 60 * 1000);
      setModalError(
        `Transferencias cerradas. El límite fue ${formatDateTime(transferDeadline.toISOString())} (15 minutos antes del inicio).`
      );
      return;
    }

    const nextPending = [...pendingTransfers];
    
    // Agregar o actualizar la transferencia principal
    if (existingIndex >= 0) {
      const originalOut = nextPending[existingIndex].playerOut;
      nextPending[existingIndex] = { slotKey, playerOut: originalOut, playerIn };
    } else {
      nextPending.push({ slotKey, playerOut, playerIn });
    }
    
    // Si se proporciona intercambio compensatorio, agregarlo también
    if (compensatorySwapOut && compensatorySwapIn) {
      const compensatorySlotKey = compensatorySwapOut.slotKey;
      const compensatoryExistingIndex = nextPending.findIndex((transfer) => transfer.slotKey === compensatorySlotKey);
      
      if (compensatoryExistingIndex >= 0) {
        const originalCompensatoryOut = nextPending[compensatoryExistingIndex].playerOut;
        nextPending[compensatoryExistingIndex] = { 
          slotKey: compensatorySlotKey, 
          playerOut: originalCompensatoryOut, 
          playerIn: compensatorySwapIn 
        };
      } else {
        nextPending.push({ 
          slotKey: compensatorySlotKey, 
          playerOut: compensatorySwapOut.player, 
          playerIn: compensatorySwapIn 
        });
      }
    }

    const updatedAssigned = cloneAssignedMap(originalAssigned);
    nextPending.forEach((transfer) => {
      updatedAssigned[transfer.slotKey] = { ...transfer.playerIn };
    });

    const teamCounts: { [key: string]: number } = {};
    Object.values(updatedAssigned).forEach((player: any) => {
      if (player?.team) {
        teamCounts[player.team] = (teamCounts[player.team] || 0) + 1;
      }
    });
    if (playerIn.team && teamCounts[playerIn.team] > 3) {
      setModalError(`Ya tienes 3 jugadores de ${playerIn.team}. Máximo 3 por club.`);
      return;
    }

    const totalDiff = nextPending.reduce((sum, transfer) => {
      const out = parseFloat(transfer.playerOut.price ?? transfer.playerOut.transferPrice ?? '0');
      const inside = parseFloat(transfer.playerIn.price ?? '0');
      const safeOutDiff = Number.isFinite(out) ? out : 0;
      const safeInDiff = Number.isFinite(inside) ? inside : 0;
      return sum + safeOutDiff - safeInDiff;
    }, 0);

    // Validar presupuesto antes de permitir la transferencia
    const newBudget = budget + totalDiff;
    if (newBudget < 0) {
      const needed = Math.abs(totalDiff - budget);
      setModalError(`No tienes suficiente presupuesto. Necesitas $${needed.toFixed(1)}M pero tienes $${budget.toFixed(1)}M`);
      // Cerrar modal de confirmación y volver a mostrar la lista de jugadores
      setShowTransferConfirmation(false);
      setShowTransferModal(true);
      // Limpiar la selección del jugador que entra para que pueda elegir otro
      setPlayerToTransferIn(null);
      return;
    }

    setAssigned(updatedAssigned);
    setPendingTransfers(nextPending);
    setDraftBudget(parseFloat((budget + totalDiff).toFixed(1)));

    if (captainId === playerOut.id?.toLowerCase() || (existingIndex >= 0 && captainId === nextPending[existingIndex]?.playerOut?.id?.toLowerCase())) {
      setCaptainId(null);
    }

    setShowTransferModal(false);
    setShowTransferConfirmation(false);
    setSelectedPlayerToTransfer(null);
    setPlayerToTransferIn(null);
    setAvailablePlayers([]); // Limpiar lista de jugadores disponibles
    setModalError(null);
  };

  const performTransferRequest = useCallback(
    async (playerOut: any, playerIn: any) => {
      if (!teamId || !currentMatchday) {
        return { success: false, message: 'No se pudo aplicar la transferencia.' };
      }

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/transfers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            playerOutId: playerOut.id,
            playerInId: playerIn.id,
            matchdayId: currentMatchday.id,
          }),
        });

        if (!response.ok) {
          let errorMessage = 'Error realizando transferencia';
          try {
            const errorData = await response.json();
            errorMessage = translateError(errorData.error || errorData.message || errorMessage);
          } catch (error) {
            console.error('Error parseando respuesta de transferencia:', error);
          }
          return { success: false, message: errorMessage };
        }

        return { success: true };
      } catch (error) {
        console.error('performTransferRequest error:', error);
        return { success: false, message: 'Error de conexión. Intenta nuevamente.' };
      }
    },
    [teamId, currentMatchday, translateError]
  );

  const applyPendingTransfers = useCallback(async () => {
    if (pendingTransfers.length === 0) return;

    // Validar si los cambios están abiertos usando cambiosAbiertos de la BD
    if (currentMatchday && !currentMatchday.cambiosAbiertos) {
      const transferDeadline = new Date(new Date(currentMatchday.startDate).getTime() - 15 * 60 * 1000);
      setModalError(
        `Transferencias cerradas. El límite fue ${formatDateTime(transferDeadline.toISOString())} (15 minutos antes del inicio).`
      );
      return;
    }

    // Validar si alguna transferencia necesita intercambio compensatorio
    for (const transfer of pendingTransfers) {
      const validation = validateTransferNeedsCompensatory(transfer.playerOut, transfer.playerIn);
      if (validation.needsSwap) {
        // Verificar si ya hay un intercambio compensatorio en las transferencias pendientes
        const hasCompensatorySwap = pendingTransfers.some(t => 
          t.slotKey !== transfer.slotKey &&
          t.playerOut.positionType === validation.oppositePosition &&
          t.playerIn.positionType === transfer.playerOut.positionType
        );
        
        if (!hasCompensatorySwap) {
          // Necesita intercambio compensatorio - mostrar mensaje y permitir seleccionarlo
          const oppositePositionName = validation.oppositePosition === 'Perimetral' ? 'perimetral' : 'interior';
          const neededPositionName = transfer.playerOut.positionType === 'Perimetral' ? 'perimetral' : 'interior';
          const currentOutPosition = transfer.playerOut.positionType === 'Perimetral' ? 'perimetral' : 'interior';
          const currentInPosition = transfer.playerIn.positionType === 'Perimetral' ? 'perimetral' : 'interior';
          
          setModalError(
            `⚠️ Esta transferencia requiere un intercambio compensatorio.\n\n` +
            `Estás cambiando un ${currentOutPosition} por un ${currentInPosition}, lo que violaría las restricciones del equipo (6 perimetrales, 4 interiores).\n\n` +
            `Para completar el cambio, necesitas también transferir un ${oppositePositionName} de tu equipo por un ${neededPositionName} del mercado.\n\n` +
            `Agrega esa segunda transferencia y luego guarda los cambios.`
          );
          return;
        }
      }
    }

    // Validar límite de transferencias
    if (transfersUsed + pendingTransfers.length > maxTransfers) {
      setModalError(`Ya alcanzaste el límite de ${maxTransfers} transferencias para esta jornada.`);
      return;
    }

    // Calcular el nuevo presupuesto ANTES de hacer las transferencias (optimistic update)
    const totalDiff = pendingTransfers.reduce((sum, transfer) => {
      const out = parseFloat(transfer.playerOut.price ?? transfer.playerOut.transferPrice ?? '0');
      const inside = parseFloat(transfer.playerIn.price ?? '0');
      const safeOutDiff = Number.isFinite(out) ? out : 0;
      const safeInDiff = Number.isFinite(inside) ? inside : 0;
      return sum + safeOutDiff - safeInDiff;
    }, 0);
    const previousBudget = budget; // Guardar el presupuesto original para poder revertir si falla
    const newBudget = Math.max(0, parseFloat((budget + totalDiff).toFixed(2)));
    
    // Actualizar el presupuesto inmediatamente (optimistic update) para que se vea el valor correcto de inmediato
    setBudget(newBudget);
    setDraftBudget(null); // Limpiar draftBudget para que no interfiera

    setIsApplyingTransfers(true);
    try {
      let captainRemoved = false;

      for (const transfer of pendingTransfers) {
        if (captainId && transfer.playerOut.id?.toLowerCase() === captainId) {
          captainRemoved = true;
        }

        const result = await performTransferRequest(transfer.playerOut, transfer.playerIn);
        if (!result.success) {
          // Si falla, revertir el presupuesto optimista al valor original
          setBudget(previousBudget);
          setModalError(result.message || 'Error realizando transferencia');
          await loadExistingPlayers();
          await fetchBudget();
          await fetchTransfersUsed();
          await fetchCaptain();
          setPendingTransfers([]);
          setDraftBudget(null);
          return;
        }
      }

      console.log('🔄 Recargando datos después de aplicar transferencias...');
      // Hacer todas las llamadas en paralelo para mayor velocidad
      // fetchBudget() confirmará el valor desde el servidor
      await Promise.all([
        loadExistingPlayers(),
        fetchBudget(), // Esto confirmará el valor optimista con el valor real del servidor
        fetchTransfersUsed(),
        fetchCaptain()
      ]);
      console.log('✅ Todos los datos recargados');

      if (captainRemoved && originalCaptainId) {
        setCaptainId(originalCaptainId);
      }

      setPendingTransfers([]);
      setModalError(null);
    } finally {
      setIsApplyingTransfers(false);
    }
  }, [pendingTransfers, currentMatchday, performTransferRequest, loadExistingPlayers, fetchBudget, fetchTransfersUsed, fetchCaptain, captainId, originalCaptainId, budget, maxTransfers, transfersUsed, validateTransferNeedsCompensatory]);

  // Realizar transferencia
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTransfer = async (playerOut: any, playerIn: any) => {
    if (transfersUsed >= maxTransfers) {
      setModalError(`Ya has usado todas tus transferencias (${maxTransfers}) para esta jornada`);
      // Cerrar modales de transferencia para que el error sea visible
      setShowTransferModal(false);
      setShowTransferConfirmation(false);
      setSelectedPlayerToTransfer(null);
      setPlayerToTransferIn(null);
      setAvailablePlayers([]); // Limpiar lista de jugadores disponibles
      return;
    }

    // Validar si los cambios están abiertos usando cambiosAbiertos de la BD
    if (currentMatchday && !currentMatchday.cambiosAbiertos) {
      const transferDeadline = new Date(new Date(currentMatchday.startDate).getTime() - 15 * 60 * 1000);
      setModalError(`Transferencias cerradas. El límite para transferencias fue el ${formatDateTime(transferDeadline.toISOString())} (15 minutos antes del inicio de la jornada)`);
      // Cerrar modales de transferencia para que el error sea visible
      setShowTransferModal(false);
      setShowTransferConfirmation(false);
      setSelectedPlayerToTransfer(null);
      setPlayerToTransferIn(null);
      setAvailablePlayers([]); // Limpiar lista de jugadores disponibles
        return;
      }

    // Validar máximo 3 jugadores del mismo club
    const currentPlayers = Object.entries(assigned)
      .map(([, player]) => player)
      .filter(Boolean)
      .filter((p: any) => p.id !== playerOut.id); // Excluir al jugador que sale
    
    const teamCounts: { [key: string]: number } = {};
    currentPlayers.forEach((player: any) => {
      if (player.team) {
        teamCounts[player.team] = (teamCounts[player.team] || 0) + 1;
      }
    });
    
    // Si ya hay 3 del equipo del jugador entrante, bloquear
    if (playerIn.team && teamCounts[playerIn.team] && teamCounts[playerIn.team] >= 3) {
      setModalError(`Ya tienes 3 jugadores de ${playerIn.team}. Máximo 3 por club.`);
      // Cerrar modales de transferencia para que el error sea visible
      setShowTransferModal(false);
      setShowTransferConfirmation(false);
      setSelectedPlayerToTransfer(null);
      setPlayerToTransferIn(null);
      setAvailablePlayers([]); // Limpiar lista de jugadores disponibles
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Verificar si el jugador que sale es el capitán
      const isCaptainLeaving = captainId === playerOut.id.toString();
      
      const transferRes = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/transfers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          playerOutId: playerOut.id,
          playerInId: playerIn.id,
          matchdayId: currentMatchday.id
        }),
      });
      
      if (!transferRes.ok) {
        const errorData = await transferRes.json();
        console.error('Error en transferencia:', errorData);
        setModalError(translateError(errorData.error || errorData.message || 'Error realizando transferencia'));
        // Cerrar modales de transferencia para que el error sea visible
        setShowTransferModal(false);
        setShowTransferConfirmation(false);
        setSelectedPlayerToTransfer(null);
        setPlayerToTransferIn(null);
        setAvailablePlayers([]); // Limpiar lista de jugadores disponibles
        return;
      }
      
      // Recargar datos primero
      await loadExistingPlayers();
      await fetchBudget();
      await fetchTransfersUsed();
      
      // Si el capitán se transfiere, necesitamos asignar el capitán a otro titular
      if (isCaptainLeaving) {
        console.log('🔄 El capitán se transfiere, buscando nuevo capitán entre los titulares...');
        const titulares = Object.entries(assigned)
          .filter(([key, player]) => key.startsWith('starter') && player && player.id !== playerOut.id)
          .map(([, player]) => player);
        if (titulares.length > 0) {
          console.log('🔄 Nuevo capitán provisional:', titulares[0].name);
          await handleSetCaptain(titulares[0].id);
          } else {
          console.warn('⚠️ No se encontró titular para asignar como capitán provisional');
          }
        }
      
      // Cerrar modales y limpiar selecciones
      setShowTransferModal(false);
      setShowTransferConfirmation(false);
      setSelectedPlayerToTransfer(null);
      setPlayerToTransferIn(null);
      setAvailablePlayers([]); // Limpiar lista de jugadores disponibles
      setModalError(null);
    } catch (error) {
      console.error('Error realizando transferencia:', error);
      setModalError('Error realizando la transferencia. Revisa tu conexión e intenta nuevamente.');
      setShowTransferModal(false);
      setShowTransferConfirmation(false);
      setSelectedPlayerToTransfer(null);
      setPlayerToTransferIn(null);
      setAvailablePlayers([]); // Limpiar lista de jugadores disponibles
    }
  };

  // Obtener suplentes de la misma posición para sustitución
  const getSubstitutesOfSamePosition = (positionType: string, currentSlotType: 'starter' | 'bench', currentSlotIdx: number) => {
    const substitutes: any[] = [];
    
    // Buscar en el otro tipo de slot (si es titular, buscar en suplentes y viceversa)
    const targetSlotType = currentSlotType === 'starter' ? 'bench' : 'starter';
    const targetSlots = targetSlotType === 'starter' ? starters : bench;
    
    for (let i = 0; i < targetSlots.length; i++) {
      const key = `${targetSlotType}-${i}`;
      const player = assigned[key];
      
      if (player && player.positionType === positionType) {
        substitutes.push({
          ...player,
          slotType: targetSlotType,
          slotIdx: i
        });
      }
    }
    
    return substitutes;
  };

  // Realizar sustitución entre jugadores
  const handleSubstitution = async (playerToSubstitute: any, targetSlotType: 'starter' | 'bench', targetSlotIdx: number) => {
    if (!slotToRemove || !teamId) {
      console.log('No hay slot para sustituir o falta teamId');
      return;
    }
    if (isProcessingSubstitution) {
      console.log('Sustitución en curso, ignorando clic adicional');
        return;
      }
    setIsProcessingSubstitution(true);
    
    
    const sourceSlotType = slotToRemove.type;
    const sourceSlotIdx = slotToRemove.idx;
    const sourcePlayer = playerToRemove;
    
    console.log('Realizando sustitución:', {
      sourcePlayer: sourcePlayer.name,
      targetPlayer: playerToSubstitute.name,
      sourceSlot: `${sourceSlotType}-${sourceSlotIdx}`,
      targetSlot: `${targetSlotType}-${targetSlotIdx}`
    });
    
    try {
      const token = localStorage.getItem('token');
      setShowSubstitutionModal(false);
      setSubstitutionMode(null);
      setPlayerToRemove(null);
      setSlotToRemove(null);
      setModalError(null);
      
      console.log('🔍 Iniciando sustitución con datos:', {
        teamId,
        sourcePlayerId: sourcePlayer.id,
        targetPlayerId: playerToSubstitute.id,
        sourceRole: targetSlotType === 'starter' ? 'Titular' : 'Suplente',
        targetRole: sourceSlotType === 'starter' ? 'Titular' : 'Suplente'
      });
      
      // Actualizar roles de ambos jugadores
      const updateSourceRes = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/players/${sourcePlayer.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          role: targetSlotType === 'starter' ? 'Titular' : 'Suplente'
        }),
      });
      
      const updateTargetRes = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/players/${playerToSubstitute.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          role: sourceSlotType === 'starter' ? 'Titular' : 'Suplente'
        }),
      });
      
      console.log('🔍 Respuestas de API:', {
        updateSourceRes: { status: updateSourceRes.status, ok: updateSourceRes.ok },
        updateTargetRes: { status: updateTargetRes.status, ok: updateTargetRes.ok }
      });
      
      if (!updateSourceRes.ok) {
        const errorData = await updateSourceRes.json();
        console.error('Error del backend (source):', errorData);
        setModalError(translateError(errorData.error || errorData.message || 'Error updating source player role'));
        return;
      }
      
      if (!updateTargetRes.ok) {
        const errorData = await updateTargetRes.json();
        console.error('Error del backend (target):', errorData);
        setModalError(translateError(errorData.error || errorData.message || 'Error updating target player role'));
        return;
      }
      
      // Actualizar estado local
      setAssigned((prev: any) => {
        const newAssigned = { ...prev };
        newAssigned[`${sourceSlotType}-${sourceSlotIdx}`] = playerToSubstitute;
        newAssigned[`${targetSlotType}-${targetSlotIdx}`] = sourcePlayer;
        return newAssigned;
      });
      
      // Transferir capitanía si el capitán salió de titular
      if (sourceSlotType === 'starter' && captainId === sourcePlayer.id?.toLowerCase()) {
        // El capitán salió de titular, transferir capitanía al jugador que entra
        await handleSetCaptain(playerToSubstitute.id);
      }
      
      // Recargar jugadores existentes
      await loadExistingPlayers();
      
      console.log('✅ Sustitución realizada exitosamente');
    } catch (error) {
      console.error('Error en handleSubstitution:', error);
      setModalError(translateError('Network error'));
    } finally {
      setIsProcessingSubstitution(false);
    }
  };

  // Efectos
  useEffect(() => {
    loadExistingPlayers();
    fetchBudget();
    fetchCurrentMatchday();
    fetchCaptain();
  }, [loadExistingPlayers, fetchBudget, fetchCurrentMatchday, fetchCaptain]);

  useEffect(() => {
    fetchMatchdayTimeline();
  }, [fetchMatchdayTimeline]);

  // Hacer scroll automático a la fecha actual cuando se carguen las fechas
  useEffect(() => {
    if (!loadingTimeline && timelineMatchdays.length > 0 && timelineScrollRef.current) {
      const nextMatchdayId = nextMatchdayInfo?.id;
      const nextIndex = timelineMatchdays.findIndex((md: any) => md.id === nextMatchdayId);
      
      // La fecha actual es la próxima o la anterior a la próxima
      const currentIndex = nextIndex >= 0 ? nextIndex : timelineMatchdays.findIndex((md: any) => 
        md.status === 'in_progress' || md.status === 'completed'
      );
      
      if (currentIndex >= 0) {
        // Esperar un momento para que el DOM se renderice
        setTimeout(() => {
          const scrollContainer = timelineScrollRef.current;
          if (scrollContainer) {
            // Calcular la posición del elemento actual
            // Cada elemento tiene aproximadamente 120px de ancho + 8px de gap = 128px
            const itemWidth = 128;
            const scrollPosition = currentIndex * itemWidth - (scrollContainer.clientWidth / 2) + (itemWidth / 2);
            
            scrollContainer.scrollTo({
              left: Math.max(0, scrollPosition),
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    }
  }, [loadingTimeline, timelineMatchdays, nextMatchdayInfo]);

  // Auto-cerrar modal de error después de un tiempo (más largo para errores importantes)
  useEffect(() => {
    if (modalError) {
      // Si es un error de intercambio compensatorio, mostrar por más tiempo (10 segundos)
      const isCompensatoryError = modalError.includes('intercambio compensatorio') || modalError.includes('Necesitas agregar otra transferencia');
      const timeoutDuration = isCompensatoryError ? 10000 : 5000; // 10 segundos para errores importantes, 5 para otros
      
      const timer = setTimeout(() => {
        setModalError(null);
      }, timeoutDuration);

      return () => clearTimeout(timer);
    }
  }, [modalError]);

  useEffect(() => {
    console.log('🔄 [useEffect] currentMatchday cambió:', currentMatchday);
    console.log('🔄 [useEffect] cambiosAbiertos:', currentMatchday?.cambiosAbiertos);
    if (currentMatchday) {
      fetchTransfersUsed();
    }
  }, [currentMatchday, fetchTransfersUsed]);

  // Asignar capitán por defecto cuando se carga el capitán y no hay uno
  useEffect(() => {
    if (captainLoaded && !captainId) {
      const titulares = Object.values(assigned).filter((player: any) => 
        player && player.role === 'Titular'
      );
      
      if (titulares.length > 0) {
        const firstStarter = titulares[0];
        console.log(`🔄 Asignando capitán por defecto desde useEffect: ${firstStarter.name}`);
        handleSetCaptain(firstStarter.id);
      }
    }
  }, [captainLoaded, captainId, assigned, handleSetCaptain]);

  useEffect(() => {
    if (showTransferModal && selectedPlayerToTransfer) {
      // Cargar jugadores inmediatamente
      fetchAvailablePlayers(selectedPlayerToTransfer);
    } else if (!showTransferModal) {
      // Limpiar cuando se cierra el modal
      setAvailablePlayers([]);
      setLoadingAvailablePlayers(false);
    }
  }, [showTransferModal, selectedPlayerToTransfer, fetchAvailablePlayers]);

  // Filtrar jugadores disponibles para transferencia
  const filteredAvailablePlayers = useMemo(() => {
    return availablePlayers.filter((player: any) => {
      const matchesSearch = player.name.toLowerCase().includes(transferSearchTerm.toLowerCase()) ||
                           player.team.toLowerCase().includes(transferSearchTerm.toLowerCase());
      const matchesCategory = transferCategoryFilter.length === 0 || transferCategoryFilter.includes(player.category);
      const matchesClub = transferSelectedClubs.length === 0 || transferSelectedClubs.includes(player.team);
      const matchesPosition = !transferSelectedPosition || player.positionType === transferSelectedPosition;
      const playerPrice = parseFloat(player.price || 0);
      const matchesPrice = playerPrice >= transferPriceRange[0] && playerPrice <= transferPriceRange[1];
      return matchesSearch && matchesCategory && matchesClub && matchesPosition && matchesPrice;
    }).sort((a, b) => {
      if (transferSort === 'price_asc' || transferSort === 'price_desc') {
        const priceA = parseFloat(a.price || 0);
        const priceB = parseFloat(b.price || 0);
        return transferSort === 'price_asc' ? priceA - priceB : priceB - priceA;
      }
      if (transferSort === 'fantasy_asc' || transferSort === 'fantasy_desc') {
        const pointsA = parseFloat(a.totalFantasyPoints || 0);
        const pointsB = parseFloat(b.totalFantasyPoints || 0);
        return transferSort === 'fantasy_asc' ? pointsA - pointsB : pointsB - pointsA;
      }
      if (transferSort === 'avg_asc' || transferSort === 'avg_desc') {
        const avgA = parseFloat(a.averagePointsPerGame || 0);
        const avgB = parseFloat(b.averagePointsPerGame || 0);
        return transferSort === 'avg_asc' ? avgA - avgB : avgB - avgA;
      }
      return 0;
    });
  }, [availablePlayers, transferSearchTerm, transferCategoryFilter, transferSelectedClubs, transferSelectedPosition, transferPriceRange, transferSort]);



  const pendingTransfersCount = pendingTransfers.length;
  const previewTransfersUsed = transfersUsed + pendingTransfersCount;
  const displayedBudget = draftBudget !== null ? draftBudget : budget;

    return (
    <div className="min-h-screen">
      {/* ── HEADER compacto ── */}
      <div className="px-3 pt-2 pb-2 -mt-4">
        <div className="flex items-center gap-2 rounded-xl px-3 py-2" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {/* Nombre */}
          <h1 className="flex-1 text-sm font-black text-white uppercase leading-none truncate">{teamName}</h1>
          {/* Puntos */}
          <div className="flex items-center gap-1">
            <span className="text-[9px] font-bold text-white/40 uppercase">PTS</span>
            <span className="text-sm font-black text-white">{timelineMatchdays.reduce((sum: number, md: any) => sum + (md.points || 0), 0)}</span>
          </div>
          <div className="w-px h-4 bg-white/10" />
          {/* Presupuesto */}
          <div className="flex items-center gap-1">
            <span className="text-[9px] font-bold text-white/40 uppercase">$</span>
            <span className="text-sm font-black text-lub-accent">{formatNumber(displayedBudget)}M</span>
          </div>
          <div className="w-px h-4 bg-white/10" />
          {/* Transferencias */}
          <div className="flex items-center gap-1">
            <svg className="w-3 h-3 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
            <span className="text-sm font-black text-purple-300">{Math.max(0, maxTransfers - previewTransfersUsed)}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-2 sm:px-3 md:px-4">
        {/* Timeline compacto */}
        <div className="rounded-xl border border-white/10 px-2 pt-1.5 pb-1 mb-2" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] font-bold text-white/40 uppercase tracking-widest">Fechas</span>
            <button
              type="button"
              onClick={() => navigate('/Fechas')}
              className="text-[9px] text-lub-accent hover:text-yellow-300"
            >
              Ver todo →
            </button>
          </div>

          {loadingTimeline ? (
            <div className="flex items-center justify-center py-2 text-[10px] text-white/40">
              <div className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-lub-accent mr-2"></div>
              Cargando...
            </div>
          ) : timelineError ? (
            <div className="text-red-400 text-[10px] px-1 py-1 rounded-lg">
              {timelineError}
            </div>
          ) : (
            <div className="overflow-x-auto" ref={timelineScrollRef} style={{ WebkitOverflowScrolling: 'touch' }}>
              {(() => {
                const nextMatchdayId = nextMatchdayInfo?.id;
                const nextIndex = timelineMatchdays.findIndex((md: any) => md.id === nextMatchdayId);
    return (
                  <div className="flex gap-1.5 min-w-max py-0.5">
                    {timelineMatchdays.map((item: any, index: number) => {
                      const isNext = item.id === nextMatchdayId;
                      const isPreviousToNext = nextIndex > 0 && index === nextIndex - 1;
                      const statusChip = isNext ? 'Próxima' : isPreviousToNext ? 'Actual' : undefined;

                      const baseStyle = {
                        background: isNext ? 'rgba(255,184,0,0.12)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${isNext ? 'rgba(255,184,0,0.35)' : 'rgba(255,255,255,0.08)'}`,
                      };

                      return (
                        <div key={item.id}>
                          {isNext ? (
                            <button
                              type="button"
                              onClick={() =>
                                document
                                  .getElementById('team-editor')
                                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                              }
                              className="flex flex-col items-center px-2.5 py-1.5 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                              style={baseStyle}
                            >
                              {statusChip && (
                                <span className="text-[7px] font-bold px-1 py-0.5 rounded-full bg-lub-accent text-lub-dark mb-0.5 leading-none">
                                  {statusChip}
                                </span>
                              )}
                              <div className="text-[10px] font-semibold text-white leading-tight">{item.name}</div>
                              <div className="text-[9px] text-white/40">editar</div>
                            </button>
                          ) : item.status === 'completed' || item.status === 'in_progress' ? (
                            <button
                              type="button"
                              onClick={() => handleViewMatchdayInFechas(item.id)}
                              className="flex flex-col items-center px-2.5 py-1.5 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                              style={baseStyle}
                            >
                              {statusChip && (
                                <span className="text-[7px] font-bold px-1 py-0.5 rounded-full bg-lub-accent text-lub-dark mb-0.5 leading-none">
                                  {statusChip}
                                </span>
                              )}
                              <div className="text-[10px] font-semibold text-white leading-tight">{item.name}</div>
                              <div className={`text-[11px] font-bold leading-none mt-0.5 ${item.points > 0 ? 'text-green-400' : 'text-white/30'}`}>
                                {item.points} pts
                              </div>
                            </button>
                          ) : (
                            <div
                              className="flex flex-col items-center px-2.5 py-1.5 rounded-lg opacity-50"
                              style={baseStyle}
                            >
                              {statusChip && (
                                <span className="text-[7px] font-bold px-1 py-0.5 rounded-full bg-lub-accent text-lub-dark mb-0.5 leading-none">
                                  {statusChip}
                                </span>
                              )}
                              <div className="text-[10px] font-semibold text-white leading-tight">{item.name}</div>
                              <div className="text-[9px] text-white/30">pronto</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {pendingTransfersCount > 0 && (
          <div className="bg-amber-500/10 border border-amber-400/30 rounded-xl p-2 sm:p-3 mb-3 flex flex-col gap-2 sm:gap-3">
            <div className="text-xs sm:text-sm text-amber-300 font-semibold">
              Tenés {pendingTransfersCount} transferencia(s) pendientes.
            </div>
            <div className="bg-white/5 border border-amber-400/20 rounded-lg p-2 sm:p-3 text-xs sm:text-sm text-amber-200 space-y-2">
              {pendingTransfers.map((transfer, idx) => (
                <div key={`${transfer.slotKey}-${idx}`} className="flex items-start sm:items-center justify-between gap-2 sm:gap-3 py-2 border-b border-amber-200 last:border-b-0">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-xs sm:text-sm break-words">
                      Sale {transfer.playerOut.name} — Entra {transfer.playerIn.name}
              </div>
                    <div className="text-[10px] sm:text-xs text-amber-300/70 mt-1">
                      {transfer.playerOut.positionType} → {transfer.playerIn.positionType} • {transfer.playerOut.category}
            </div>
              </div>
                  <button
                    type="button"
                    onClick={() => discardSingleTransfer(transfer.slotKey)}
                    className="px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors flex-shrink-0"
                    disabled={isApplyingTransfers}
                    title="Descartar esta transferencia"
                  >
                    ✕
                  </button>
            </div>
              ))}
          </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <button
                type="button"
                onClick={discardPendingTransfers}
                className="w-full sm:w-auto px-3 py-1.5 text-xs font-semibold text-amber-800 border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors"
                disabled={isApplyingTransfers}
              >
                Descartar todas
              </button>
              <button
                type="button"
                onClick={applyPendingTransfers}
                className="w-full sm:w-auto px-3 py-1.5 text-xs font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-60"
                disabled={isApplyingTransfers}
              >
                {isApplyingTransfers ? 'Guardando…' : 'Guardar cambios'}
              </button>
        </div>
      </div>
        )}

        <div id="team-editor" className="flex flex-col lg:flex-row gap-4">
          {/* Cancha de Basketball - Lado Izquierdo */}
          <div className="flex-1 order-2 lg:order-1">
            <div className="pt-0 -mt-1">

              {/* Cancha — posicionamiento absoluto sobre imagen de madera */}
              <BasketballCourt>
                {/* altura fija para que quepan bien las camisetas */}
                <div className="relative w-full" style={{ height: '260px' }}>
                  {starters.map((slot, idx) => {
                    const key = `starter-${idx}`;
                    const assignedPlayer = assigned[key];
                    return (
                      <div
                        key={idx}
                        className="absolute"
                        style={{
                          left: `${slot.x}%`,
                          top: `${slot.y}%`,
                          transform: 'translate(-50%, -50%)',
                        }}
                      >
                        <JerseySlot
                          player={assignedPlayer}
                          isCaptain={captainId === assignedPlayer?.id?.toLowerCase()}
                          onClick={() => {
                            if (assignedPlayer) {
                              setPlayerToRemove(assignedPlayer);
                              setSlotToRemove({ type: 'starter', idx });
                              setShowSubstitutionModal(true);
                              setSubstitutionMode(null);
                            }
                          }}
                          isStarter={true}
                          canChangeCaptain={false}
                          slotPositionType={slot.positionType}
                        />
                      </div>
                    );
                  })}
                </div>
              </BasketballCourt>

              {/* ── BANCO ── */}
              <div className="rounded-2xl overflow-hidden mt-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <p className="px-4 pt-3 pb-2 text-xs font-black text-white/50 uppercase tracking-widest">Banco</p>
                {bench.map((slot, idx) => {
                  const key = `bench-${idx}`;
                  const player = assigned[key];
                  const posLabel = slot.positionType === 'Interior' ? 'INT' : 'PER';
                  const pts = player ? ((player as any).fantasyPoints ?? 0) : 0;
                  return (
                    <div
                      key={idx}
                      onClick={() => {
                        if (player) {
                          setPlayerToRemove(player);
                          setSlotToRemove({ type: 'bench', idx });
                          setShowSubstitutionModal(true);
                          setSubstitutionMode(null);
                        }
                      }}
                      className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-white/5 transition-colors"
                      style={{ borderTop: idx > 0 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}
                    >
                      {/* Número */}
                      <div className="w-6 h-6 rounded-full bg-lub-blue flex items-center justify-center flex-shrink-0">
                        <span className="text-[10px] font-black text-white">{idx + 1}</span>
                      </div>

                      {/* Jersey circle */}
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ background: '#0a1628', border: '2px solid #1e3a5f' }}>
                        {player
                          ? <img src={getJerseyImage(player.team)} alt={player.team} className="w-full h-full object-contain" />
                          : <span className="text-[9px] text-white/30 font-bold">{posLabel}</span>
                        }
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white truncate">{player?.name ?? `Slot ${posLabel}`}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,87,184,0.3)', color: '#93c5fd' }}>
                            {posLabel}
                          </span>
                          <span className={`text-[11px] font-semibold ${pts > 0 ? 'text-green-400' : 'text-white/30'}`}>
                            {pts} PTS
                          </span>
                        </div>
                      </div>

                      {/* Acción */}
                      {player && (
                        <svg className="w-5 h-5 text-white/30 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar de Información - Lado Derecho */}
          <div className="w-full lg:w-80 order-1 lg:order-2">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-3 sm:p-4 lg:p-5">
              <div className="mb-4">
                <h2 className="text-base font-bold text-white mb-3">Próxima fecha</h2>
                {currentMatchday ? (
                  <div className="space-y-2">
                    {/* Nombre + ver partidos */}
                    <div className="flex items-center justify-between bg-lub-blue/20 rounded-xl px-3 py-2 border border-lub-blue/30">
                      <span className="text-sm font-semibold text-white">{currentMatchday.name}</span>
                      <button
                        onClick={() => { setShowMatchesModal(true); if (currentMatchday?.name) fetchFixtures(currentMatchday.name); }}
                        className="px-2 py-1 text-xs bg-lub-blue hover:bg-lub-light text-white rounded-lg transition-colors flex items-center gap-1"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Partidos
                      </button>
                    </div>

                    {/* Stats row */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white/5 rounded-xl p-2 border border-white/10">
                        <p className="text-[10px] text-blue-300 mb-0.5">Transferencias</p>
                        <p className="text-sm font-bold text-white">{previewTransfersUsed}<span className="text-white/40 font-normal">/{maxTransfers}</span></p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-2 border border-white/10">
                        <p className="text-[10px] text-blue-300 mb-0.5">Presupuesto</p>
                        <p className="text-sm font-bold text-lub-accent">${formatNumber(displayedBudget)}M</p>
                      </div>
                    </div>

                    {matchdayStartDate && (
                      <div className="bg-white/5 rounded-xl px-3 py-2 border border-white/10">
                        <p className="text-[10px] text-blue-300">Inicio de la jornada</p>
                        <p className="text-xs text-white mt-0.5">{matchdayStartDate}</p>
                      </div>
                    )}

                    {transferDeadline && (
                      <div className="bg-red-500/10 rounded-xl px-3 py-2 border border-red-400/20">
                        <p className="text-[10px] text-red-400">Límite transferencias</p>
                        <p className="text-xs text-red-300 mt-0.5">{transferDeadline}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-white/40">Sin jornada próxima</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notificación de error */}
      {modalError && (
        <div className="fixed top-2 sm:top-4 left-1/2 transform -translate-x-1/2 z-[9999] max-w-md w-[calc(100%-1rem)] sm:w-full mx-2 sm:mx-4" style={{ touchAction: 'none' }}>
          <div className="bg-red-500/95 backdrop-blur-sm border border-red-400/50 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-xl shadow-2xl">
            <div className="flex items-center">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-full flex items-center justify-center mr-2 sm:mr-3 border-2 border-red-300 flex-shrink-0">
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs sm:text-sm font-medium break-words">{modalError}</div>
              </div>
              <button
                onClick={() => setModalError(null)}
                className="ml-2 text-white hover:text-red-200 transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal jugador */}
      {showSubstitutionModal && playerToRemove && slotToRemove && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowSubstitutionModal(false);
              setSubstitutionMode(null);
              setPlayerToRemove(null);
              setSlotToRemove(null);
            }
          }}
        >
          <div
            className="w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl overflow-hidden animate-fade-in"
            style={{ background: '#0d1b2e', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            {/* ── HEADER ── */}
            <div className="flex items-center gap-3 px-5 pt-5 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              {/* Avatar con jersey */}
              <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-lub-blue flex-shrink-0 bg-lub-dark flex items-center justify-center">
                <img
                  src={getJerseyImage(playerToRemove.team)}
                  alt={playerToRemove.team}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Nombre + info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-white leading-tight truncate">{playerToRemove.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-lub-blue/40 text-blue-300 tracking-wide uppercase">
                    {playerToRemove.positionType === 'Perimetral' ? 'PER' : 'INT'}
                  </span>
                  <span className="text-xs text-white/60 truncate">{formatTeamName(playerToRemove.team)}</span>
                </div>
              </div>

              {/* Cerrar */}
              <button
                onClick={() => { setShowSubstitutionModal(false); setSubstitutionMode(null); setPlayerToRemove(null); setSlotToRemove(null); }}
                className="w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* ── PANTALLA PRINCIPAL ── */}
            {!substitutionMode && (
              <>
                {/* Stats grid */}
                <div className="grid grid-cols-2 gap-2 p-4">
                  <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <p className="text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">Precio</p>
                    <p className="text-2xl font-bold text-white">${playerToRemove.price}M</p>
                  </div>
                  <div className="rounded-2xl p-4" style={{ background: 'rgba(0,87,184,0.25)', border: '1px solid rgba(0,87,184,0.4)' }}>
                    <p className="text-[10px] font-semibold text-blue-300/70 uppercase tracking-widest mb-2">Categoría</p>
                    <p className="text-sm font-bold text-blue-300 leading-tight">
                      {(playerToRemove as any).category === 'FichaNacional' ? 'Ficha Nac.' :
                       (playerToRemove as any).category === 'Extranjero' ? 'Extranjero' : 'Sub 23'}
                    </p>
                  </div>
                  <div className="rounded-2xl p-4" style={{ background: 'rgba(0,87,184,0.15)', border: '1px solid rgba(0,87,184,0.25)' }}>
                    <p className="text-[10px] font-semibold text-blue-300/70 uppercase tracking-widest mb-2">Pts aportados</p>
                    <p className="text-2xl font-bold text-lub-blue">
                      {(playerToRemove as any).displayPoints ?? (playerToRemove as any).totalFantasyPoints ?? 0}
                    </p>
                  </div>
                  <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <p className="text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">Próximo rival</p>
                    {upcomingOpponent ? (
                      <p className="text-xs font-semibold text-white/80 leading-snug">
                        {upcomingOpponent?.condition} {upcomingOpponent?.opponent}
                      </p>
                    ) : (
                      <p className="text-lg font-bold text-white/20">—</p>
                    )}
                  </div>
                </div>

                {/* Botones de acción */}
                <div className="flex flex-col gap-2 px-4 pb-6">
                  <button
                    onClick={() => setSubstitutionMode('substitute')}
                    className="w-full py-3.5 rounded-2xl font-bold text-sm text-white tracking-wide flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
                    style={{ background: '#0057B8' }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    SUSTITUIR
                  </button>

                  {slotToRemove?.type === 'starter' && captainId !== String(playerToRemove.id).toLowerCase() && (
                    <button
                      onClick={() => setSubstitutionMode('captain')}
                      className="w-full py-3.5 rounded-2xl font-bold text-sm tracking-wide flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
                      style={{ background: 'transparent', border: '2px solid #FFB800', color: '#FFB800' }}
                    >
                      <span>★</span> HACER CAPITÁN
                    </button>
                  )}

                  <button
                    onClick={() => {
                      const slotKey = slotToRemove ? `${slotToRemove.type}-${slotToRemove.idx}` : null;
                      setShowSubstitutionModal(false);
                      setSubstitutionMode(null);
                      setPlayerToRemove(null);
                      setSlotToRemove(null);
                      setSelectedPlayerToTransfer(slotKey ? { ...playerToRemove, slotKey } : playerToRemove);
                      setShowTransferModal(true);
                    }}
                    className="w-full py-3.5 rounded-2xl font-bold text-sm tracking-wide flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
                    style={{ background: 'transparent', border: '2px solid #22c55e', color: '#22c55e' }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    TRANSFERIR
                  </button>
                </div>
              </>
            )}

            {/* ── LISTA DE SUSTITUCIÓN ── */}
            {substitutionMode === 'substitute' && (
              <div className="px-4 pb-6">
                <div className="flex items-center gap-2 mb-3 pt-4">
                  <button onClick={() => setSubstitutionMode(null)} className="text-white/50 hover:text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <h4 className="text-sm font-semibold text-white">
                    Intercambiar con {slotToRemove?.type === 'starter' ? 'suplente' : 'titular'}
                  </h4>
                </div>
                {(() => {
                  const substitutes = getSubstitutesOfSamePosition(playerToRemove.positionType, slotToRemove!.type, slotToRemove!.idx);
                  return (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {substitutes.length > 0 ? substitutes.map((sub) => (
                        <button
                          key={`${sub.slotType}-${sub.slotIdx}`}
                          onClick={() => handleSubstitution(sub, sub.slotType, sub.slotIdx)}
                          disabled={isProcessingSubstitution}
                          className="w-full p-3 rounded-xl text-left flex items-center justify-between transition-colors hover:bg-white/5 disabled:opacity-50"
                          style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                        >
                          <div>
                            <p className="text-sm font-semibold text-white">{sub.name}</p>
                            <p className="text-xs text-white/40 mt-0.5">{formatTeamName(sub.team)} · {sub.positionType}</p>
                          </div>
                          <span className="text-sm font-bold text-lub-accent">${sub.price}M</span>
                        </button>
                      )) : (
                        <p className="text-center text-white/40 text-sm py-6">
                          No hay {slotToRemove?.type === 'starter' ? 'suplentes' : 'titulares'} de la misma posición disponibles.
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── CONFIRMAR CAPITÁN ── */}
            {substitutionMode === 'captain' && (
              <div className="px-4 pb-6">
                <div className="flex items-center gap-2 mb-4 pt-4">
                  <button onClick={() => setSubstitutionMode(null)} className="text-white/50 hover:text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <h4 className="text-sm font-semibold text-white">Confirmar capitán</h4>
                </div>
                <div className="rounded-2xl p-4 mb-4" style={{ background: 'rgba(255,184,0,0.1)', border: '1px solid rgba(255,184,0,0.3)' }}>
                  <p className="text-sm text-lub-accent font-medium">
                    <strong>{playerToRemove.name}</strong> será tu capitán y recibirá <strong>1.5× puntos</strong> esta fecha.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setSubstitutionMode(null)} className="flex-1 py-3 rounded-xl text-sm font-semibold text-white/60 hover:text-white transition-colors" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                    Cancelar
                  </button>
                  <button
                    onClick={async () => {
                      if (playerToRemove) await handleSetCaptain(playerToRemove.id);
                      setShowSubstitutionModal(false); setSubstitutionMode(null); setPlayerToRemove(null); setSlotToRemove(null);
                    }}
                    className="flex-1 py-3 rounded-xl text-sm font-bold tracking-wide transition-opacity hover:opacity-90"
                    style={{ background: '#FFB800', color: '#001A3E' }}
                  >
                    ★ Confirmar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isProcessingSubstitution && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl px-6 py-5 flex flex-col items-center gap-3 text-center">
            <svg
              className="w-8 h-8 text-blue-600 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
            <div>
              <p className="text-sm font-semibold text-gray-800">Aplicando sustitución</p>
              <p className="text-xs text-gray-500 mt-1">Esto puede tardar unos segundos…</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal de transferencia — bottom sheet igual que ConfigurarEquipo */}
      {showTransferModal && selectedPlayerToTransfer && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/60" onClick={() => {
            setShowTransferModal(false); setSelectedPlayerToTransfer(null); setAvailablePlayers([]);
            setTransferSearchTerm(''); setTransferCategoryFilter([]); setTransferSelectedClubs([]);
            setTransferSelectedPosition('');
          }} />
          <div className="relative flex flex-col rounded-t-3xl" style={{ background: '#0A1F4E', border: '1px solid rgba(255,255,255,0.1)', maxHeight: '78vh' }}>
            {/* Handle */}
            <div className="pt-3 pb-1 flex justify-center">
              <div className="w-10 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.2)' }} />
            </div>
            {/* Header */}
            <div className="px-4 pb-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-black text-white text-base">Transferir jugador</h3>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    Sale: {selectedPlayerToTransfer.name} · {filteredAvailablePlayers.length} disponibles
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowTransferModal(false); setSelectedPlayerToTransfer(null); setAvailablePlayers([]);
                    setTransferSearchTerm(''); setTransferCategoryFilter([]); setTransferSelectedClubs([]);
                    setTransferSelectedPosition('');
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full"
                  style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {/* Search */}
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'rgba(255,255,255,0.3)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Buscar jugador..."
                  value={transferSearchTerm}
                  onChange={(e) => setTransferSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-lub-accent"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}
                  autoFocus
                />
              </div>
              {/* Category chips */}
              <div className="flex gap-2 mt-2.5 overflow-x-auto pb-0.5">
                {(['Extranjero', 'FichaNacional', 'Sub23'] as const).map((cat) => {
                  const active = transferCategoryFilter.includes(cat);
                  const label = cat === 'Extranjero' ? 'EXT' : cat === 'FichaNacional' ? 'NACIONAL' : 'SUB 23';
                  return (
                    <button
                      key={cat}
                      onClick={() => setTransferCategoryFilter(active ? transferCategoryFilter.filter(c => c !== cat) : [...transferCategoryFilter, cat])}
                      className="flex-shrink-0 px-3 py-1 rounded-full text-xs font-bold transition-all"
                      style={{
                        background: active ? '#FFB800' : 'rgba(255,255,255,0.07)',
                        color: active ? '#001A3E' : 'rgba(255,255,255,0.5)',
                        border: active ? 'none' : '1px solid rgba(255,255,255,0.1)',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
                {transferCategoryFilter.length > 0 && (
                  <button
                    onClick={() => setTransferCategoryFilter([])}
                    className="flex-shrink-0 px-3 py-1 rounded-full text-xs font-bold"
                    style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}
                  >
                    Limpiar
                  </button>
                )}
              </div>
            </div>
            {/* Player list */}
            <div className="overflow-y-auto flex-1 p-3 space-y-2">
              {loadingAvailablePlayers ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-lub-accent mb-2" />
                  <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>Cargando jugadores...</p>
                </div>
              ) : filteredAvailablePlayers.length === 0 ? (
                <div className="text-center py-8 text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  No se encontraron jugadores
                </div>
              ) : (
                filteredAvailablePlayers.map((player: any) => {
                  const availableBudgetForTransfer = selectedPlayerToTransfer
                    ? (displayedBudget + parseFloat(selectedPlayerToTransfer.price || selectedPlayerToTransfer.transferPrice || 0))
                    : displayedBudget;
                  const playerInPrice = parseFloat(player.price || 0);
                  const canAfford = availableBudgetForTransfer >= playerInPrice;
                  const finalAssigned = cloneAssignedMap(originalAssigned);
                  const slotKey = selectedPlayerToTransfer?.slotKey;
                  pendingTransfers.forEach((transfer) => { finalAssigned[transfer.slotKey] = { ...transfer.playerIn }; });
                  if (slotKey) finalAssigned[slotKey] = { ...player };
                  const finalPlayers = Object.values(finalAssigned).filter((p: any) => p && p.id && p.team);
                  const teamCounts: { [key: string]: number } = {};
                  finalPlayers.forEach((p: any) => { if (p.team) teamCounts[p.team] = (teamCounts[p.team] || 0) + 1; });
                  const hasMaxPlayersFromTeam = player.team && (teamCounts[player.team] || 0) > 3;
                  const canBeTransferred = canAfford && !hasMaxPlayersFromTeam;
                  const blocked = !canBeTransferred;
                  const categoryLabel = player.category === 'Extranjero' ? 'EXT' : player.category === 'FichaNacional' ? 'NAC' : 'S23';

                  return (
                    <button
                      key={player.id}
                      disabled={blocked}
                      onClick={() => {
                        if (canBeTransferred) {
                          showTransferConfirmationModal(selectedPlayerToTransfer, player);
                        } else if (hasMaxPlayersFromTeam) {
                          setModalError(`Ya tienes 3 jugadores de ${formatTeamName(player.team)}. Máximo 3 por club.`);
                        } else {
                          setModalError(`No tienes suficiente presupuesto. Necesitas $${playerInPrice.toFixed(1)}M pero tienes $${availableBudgetForTransfer.toFixed(1)}M`);
                        }
                      }}
                      className="w-full rounded-xl px-3 py-2.5 text-left transition-all active:scale-95"
                      style={{
                        background: canBeTransferred ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.07)',
                        opacity: blocked ? 0.45 : 1,
                        cursor: blocked ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>
                          {player.sofascoreId ? (
                            <img
                              src={`https://api.sofascore.com/api/v1/player/${player.sofascoreId}/image`}
                              alt={player.name}
                              className="w-full h-full object-cover"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-xs font-black" style={{ color: 'rgba(255,255,255,0.3)' }}>
                              {player.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="font-bold text-white text-sm truncate block">{player.name}</span>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.45)' }}>{formatTeamName(player.team)}</span>
                            <span className="text-[9px] font-bold px-1 py-0.5 rounded" style={{
                              background: player.category === 'Extranjero' ? 'rgba(251,146,60,0.2)' : player.category === 'Sub23' ? 'rgba(96,165,250,0.2)' : 'rgba(255,255,255,0.1)',
                              color: player.category === 'Extranjero' ? '#fb923c' : player.category === 'Sub23' ? '#60a5fa' : 'rgba(255,255,255,0.5)',
                            }}>
                              {categoryLabel}
                            </span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="font-black text-sm" style={{ color: '#FFB800' }}>${player.price}M</div>
                          <div className="text-[11px] mt-0.5" style={{ color: 'rgba(74,222,128,0.8)' }}>
                            {Number(player.totalFantasyPoints || 0).toFixed(0)} pts
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de transferencia */}
      {showTransferConfirmation && selectedPlayerToTransfer && playerToTransferIn && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-5" style={{ background: '#0d1b2e', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-white">Confirmar Transferencia</h3>
              <button
                onClick={() => { setShowTransferConfirmation(false); setPlayerToTransferIn(null); }}
                className="w-7 h-7 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors text-lg leading-none"
              >×</button>
            </div>

            <p className="text-sm text-white/60 mb-4">¿Estás seguro de que querés realizar esta transferencia?</p>

            {/* Jugador que sale */}
            <div className="rounded-xl p-3 mb-2" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)' }}>
              <p className="text-xs font-semibold text-red-300 mb-1">Sale</p>
              <p className="text-sm font-bold text-white">{selectedPlayerToTransfer.name}</p>
              <p className="text-xs text-white/40">{formatTeamName(selectedPlayerToTransfer.team)} · ${selectedPlayerToTransfer.price}M</p>
            </div>

            {/* Jugador que entra */}
            <div className="rounded-xl p-3 mb-5" style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.25)' }}>
              <p className="text-xs font-semibold mb-1" style={{ color: '#4ade80' }}>Entra</p>
              <p className="text-sm font-bold text-white">{playerToTransferIn.name}</p>
              <p className="text-xs text-white/40">{formatTeamName(playerToTransferIn.team)} · ${playerToTransferIn.price}M</p>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => { setShowTransferConfirmation(false); setPlayerToTransferIn(null); }}
                className="px-4 py-2 text-xs font-semibold text-white/60 rounded-xl hover:bg-white/10 transition-colors"
              >Cancelar</button>
              <button
                onClick={() => { if (selectedPlayerToTransfer && playerToTransferIn) { queueTransfer(selectedPlayerToTransfer, playerToTransferIn); } }}
                className="px-4 py-2 text-xs font-semibold text-lub-dark rounded-xl transition-colors"
                style={{ background: '#FFB800' }}
              >Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Partidos */}
      {showMatchesModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl overflow-hidden max-h-[80vh] flex flex-col" style={{ background: '#0d1b2e', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="flex items-center justify-between px-5 pt-5 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 className="text-base font-semibold text-white">Partidos de {currentMatchday?.name}</h3>
              <button
                onClick={() => { setShowMatchesModal(false); setFixtures([]); }}
                className="w-7 h-7 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors text-lg leading-none"
              >×</button>
            </div>

            <div className="p-4 overflow-y-auto">
              {loadingFixtures ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lub-accent"></div>
                  <span className="ml-2 text-white/50 text-sm">Cargando partidos...</span>
                </div>
              ) : fixtures.length > 0 ? (
                <div className="space-y-2">
                  {fixtures
                    .filter((fixture, index, self) =>
                      index === self.findIndex(f =>
                        f.localTeam === fixture.localTeam && f.visitingTeam === fixture.visitingTeam
                      )
                    )
                    .map((fixture, index) => (
                      <div key={`${fixture.localTeam}-${fixture.visitingTeam}-${index}`} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <div className="flex items-center justify-center space-x-4">
                          <div className="text-center flex-1">
                            <div className="text-sm font-semibold text-white">{formatTeamName(fixture.localTeam)}</div>
                            <div className="text-[10px] text-white/40 mt-0.5">Local</div>
                          </div>
                          <div className="text-white/30 text-sm font-bold px-2">VS</div>
                          <div className="text-center flex-1">
                            <div className="text-sm font-semibold text-white">{formatTeamName(fixture.visitingTeam)}</div>
                            <div className="text-[10px] text-white/40 mt-0.5">Visitante</div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-white/40 text-sm">
                  No se encontraron partidos para esta jornada
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTeamCompetitionPage;
